import React from "react"
import {Prism as SyntaxHighlighter} from "react-syntax-highlighter"
import {dracula} from "react-syntax-highlighter/dist/cjs/styles/prism"

import globalStyles from "../../../../../App.module.scss"
import {Arrow, Box, Diagram, DiagramCaption} from "../../../Architecture/shared/Diagram"

const installExample = `kubectl create namespace argocd
kubectl apply -n argocd --server-side --force-conflicts \\
  -f https://raw.githubusercontent.com/argoproj/argo-cd/stable/manifests/install.yaml

# Wait for it, then grab the generated admin password
kubectl -n argocd rollout status deployment/argocd-server
argocd admin initial-password -n argocd

# Reach the UI/API without exposing it (https://localhost:8080)
kubectl -n argocd port-forward svc/argocd-server 8080:443

# Log the CLI in, then change the password and delete the bootstrap secret
argocd login localhost:8080 --username admin --insecure
argocd account update-password
kubectl -n argocd delete secret argocd-initial-admin-secret`

const gitApplicationExample = `# Plain manifests or a Kustomize overlay from your own Git repo
apiVersion: argoproj.io/v1alpha1
kind: Application
metadata:
  name: hello-web
  namespace: argocd                  # Applications always live in Argo CD's namespace
  finalizers:
    - resources-finalizer.argocd.argoproj.io   # delete the app's resources when the app is deleted
spec:
  project: default
  source:
    repoURL: https://github.com/me/deploy.git
    targetRevision: main             # branch, tag or commit SHA
    path: apps/hello-web/overlays/prod
  destination:
    server: https://kubernetes.default.svc   # the cluster Argo CD runs in
    namespace: hello-web
  syncPolicy:
    automated:
      prune: true                    # delete resources that were removed from Git
      selfHeal: true                 # revert manual changes made in the cluster
    syncOptions:
      - CreateNamespace=true`

const helmApplicationExample = `# A Helm chart from a chart repo, values inline
apiVersion: argoproj.io/v1alpha1
kind: Application
metadata:
  name: podinfo
  namespace: argocd
spec:
  project: default
  source:
    repoURL: https://stefanprodan.github.io/podinfo
    chart: podinfo
    targetRevision: 6.9.0            # the chart version
    helm:
      releaseName: podinfo
      valuesObject:
        replicaCount: 2
        ui:
          message: Hello from Argo CD
  destination:
    server: https://kubernetes.default.svc
    namespace: podinfo
  syncPolicy:
    automated: {}
    syncOptions:
      - CreateNamespace=true`

const multiSourceExample = `# Chart from a chart repo, values file from your own Git repo
spec:
  sources:
    - repoURL: https://stefanprodan.github.io/podinfo
      chart: podinfo
      targetRevision: 6.9.0
      helm:
        valueFiles:
          - $values/envs/prod/podinfo-values.yaml
    - repoURL: https://github.com/me/deploy.git
      targetRevision: main
      ref: values                    # exposes this repo as $values above`

const applicationSetExample = `# One Application per folder under apps/ — add a folder, get an app
apiVersion: argoproj.io/v1alpha1
kind: ApplicationSet
metadata:
  name: apps
  namespace: argocd
spec:
  goTemplate: true
  goTemplateOptions: ["missingkey=error"]
  generators:
    - git:
        repoURL: https://github.com/me/deploy.git
        revision: main
        directories:
          - path: apps/*
  template:
    metadata:
      name: '{{.path.basename}}'
    spec:
      project: default
      source:
        repoURL: https://github.com/me/deploy.git
        targetRevision: main
        path: '{{.path.path}}'
      destination:
        server: https://kubernetes.default.svc
        namespace: '{{.path.basename}}'
      syncPolicy:
        automated:
          prune: true
          selfHeal: true
        syncOptions:
          - CreateNamespace=true`

const syncWaveExample = `# Run a migration Job before every sync, and delete it once it succeeds
apiVersion: batch/v1
kind: Job
metadata:
  name: db-migrate
  annotations:
    argocd.argoproj.io/hook: PreSync
    argocd.argoproj.io/hook-delete-policy: HookSucceeded
spec:
  template:
    spec:
      restartPolicy: Never
      containers:
        - name: migrate
          image: ghcr.io/me/hello-web:1.4.0
          command: ["./migrate", "up"]
---
# Lower waves are applied (and must be healthy) before higher ones; default is 0
apiVersion: v1
kind: ConfigMap
metadata:
  name: hello-web-config
  annotations:
    argocd.argoproj.io/sync-wave: "-1"`

const repoLayoutExample = `deploy/                          # the GitOps repo — separate from application source code
├── bootstrap/
│   └── apps.yaml                # the ApplicationSet (or app-of-apps) — the only thing applied by hand
└── apps/
    ├── hello-web/
    │   ├── base/                # Deployment, Service, Ingress
    │   └── overlays/
    │       ├── staging/         # kustomization.yaml: image tag, replicas, env
    │       └── prod/
    └── podinfo/
        └── application.yaml     # Helm-based Application`

const ignoreDifferencesExample = `# Let an HPA own replicas without Argo CD reporting OutOfSync or resetting it
spec:
  ignoreDifferences:
    - group: apps
      kind: Deployment
      jsonPointers:
        - /spec/replicas
  syncPolicy:
    syncOptions:
      - RespectIgnoreDifferences=true`

const createApplicationExample = `# Create an Application imperatively — handy for experiments, but YAML in Git is the real thing
argocd app create hello-web \\
  --repo https://github.com/me/deploy.git \\
  --path apps/hello-web/overlays/prod \\
  --dest-server https://kubernetes.default.svc \\
  --dest-namespace hello-web \\
  --sync-policy automated --auto-prune --self-heal \\
  --sync-option CreateNamespace=true`

const connectExample = `# Read access to a private Git repo — deploy key over SSH, or a token over HTTPS
argocd repo add git@github.com:me/deploy.git --ssh-private-key-path ~/.ssh/deploy_key
argocd repo add https://github.com/me/deploy.git --username git --password "$TOKEN"

# A Helm chart repository
argocd repo add https://stefanprodan.github.io/podinfo --type helm --name podinfo

# Deploy to another cluster from your kubeconfig (creates a service account there)
argocd cluster add my-kube-context
argocd cluster list

# An AppProject that limits team-a to its own repos and namespaces
argocd proj create team-a
argocd proj add-source team-a 'https://github.com/me/team-a-*'
argocd proj add-destination team-a https://kubernetes.default.svc 'team-a-*'`

export default function ArgoCdCheatSheet() {
    return (
        <div className={globalStyles.article}>
            <h1>Argo CD in a Nutshell</h1>

            <p>Argo CD is a GitOps continuous-delivery controller for Kubernetes. Instead of a CI job
                pushing <code>kubectl apply</code> or <code>helm upgrade</code> into the cluster, Argo CD runs
                inside the cluster, watches a Git repository, and continuously makes the cluster match what
                Git says — so deploying becomes a merged pull request, and Git history is your audit log and
                rollback button.</p>

            <h3>The core idea: Git is the desired state</h3>
            <p>It is the same reconciliation loop Kubernetes uses, one level up. Argo CD renders the
                manifests in Git (plain YAML, Kustomize or Helm), compares them to the live objects, and
                reports two independent things: <b>sync status</b> (does the cluster match Git?) and{' '}
                <b>health</b> (are the resources actually working?). Syncing — manually or automatically —
                applies the difference.</p>

            <h3>Architecture at a glance</h3>
            <Diagram viewBox="0 0 900 250">
                <Box x={20} y={30} width={140} height={44} label="Developer" sublabel="merges a PR"/>
                <Box x={250} y={30} width={150} height={44} label="Git repo" sublabel="desired state"/>
                <Box x={490} y={30} width={170} height={44} label="Repo server" sublabel="renders Helm/Kustomize"/>

                <Box x={20} y={170} width={140} height={44} label="UI / CLI" sublabel="argocd …"/>
                <Box x={250} y={170} width={150} height={44} label="argocd-server" sublabel="API + auth"/>
                <Box x={490} y={170} width={170} height={44} label="App controller" sublabel="diff & sync" accent/>
                <Box x={740} y={170} width={140} height={44} label="Kubernetes API" sublabel="live state"/>

                <Arrow x1={160} y1={52} x2={250} y2={52} label="git push"/>
                <Arrow x1={490} y1={52} x2={400} y2={52} label="clone / poll"/>
                <Arrow x1={575} y1={74} x2={575} y2={170} label="manifests"/>
                <Arrow x1={160} y1={192} x2={250} y2={192} label="sync, diff"/>
                <Arrow x1={400} y1={192} x2={490} y2={192} label="Application CRs"/>
                <Arrow x1={660} y1={180} x2={740} y2={180} label="apply"/>
                <Arrow x1={740} y1={206} x2={660} y2={206} label="watch" dashed muted/>
            </Diagram>
            <DiagramCaption>Nothing outside the cluster needs credentials to it: Argo CD pulls from Git
                rather than CI pushing in. The repo server turns Git into plain manifests, and the application
                controller compares them to the live objects and applies the difference — every few minutes,
                or immediately on a Git webhook.
            </DiagramCaption>

            <h3>Vocabulary</h3>
            <div className={globalStyles.styledTable}>
                <table style={{minWidth: '680px'}}>
                    <thead>
                    <tr>
                        <th>Term</th>
                        <th>What it is</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><code>Application</code></td>
                        <td>A CRD linking one source (repo + path or chart) to one destination (cluster +
                            namespace) — the unit you sync, diff and roll back
                        </td>
                    </tr>
                    <tr>
                        <td><code>AppProject</code></td>
                        <td>A guard rail around Applications: which repos they may use, which clusters and
                            namespaces they may deploy to, which kinds they may create
                        </td>
                    </tr>
                    <tr>
                        <td><code>ApplicationSet</code></td>
                        <td>A template that generates many Applications — per folder, per cluster, per
                            environment, per pull request
                        </td>
                    </tr>
                    <tr>
                        <td><b>Sync status</b></td>
                        <td><code>Synced</code> or <code>OutOfSync</code> — does the live state match Git?</td>
                    </tr>
                    <tr>
                        <td><b>Health status</b></td>
                        <td><code>Healthy</code>, <code>Progressing</code>, <code>Degraded</code>,{' '}
                            <code>Suspended</code> or <code>Missing</code> — are the resources actually working?
                        </td>
                    </tr>
                    <tr>
                        <td><b>Sync</b></td>
                        <td>Apply Git's version to the cluster</td>
                    </tr>
                    <tr>
                        <td><b>Refresh</b></td>
                        <td>Re-read Git and re-compare without applying anything; a <i>hard</i> refresh also
                            discards the cached rendered manifests
                        </td>
                    </tr>
                    <tr>
                        <td><b>Prune</b></td>
                        <td>Delete live resources that no longer exist in Git — off unless you enable it</td>
                    </tr>
                    <tr>
                        <td><b>Self-heal</b></td>
                        <td>With auto-sync, also revert changes made directly in the cluster, not just new
                            commits
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <h3>Install and log in</h3>
            <SyntaxHighlighter
                children={installExample}
                style={dracula}
                PreTag="div"
                language={'bash'}
            />
            <p>For anything beyond a lab, install it with the official <code>argo/argo-cd</code> Helm chart
                instead, so the installation itself is versioned and configurable.</p>

            <h3>Defining an Application</h3>
            <p>You can create Applications with the CLI or UI, but writing them as YAML and keeping them in
                Git is the point — then even Argo CD's own configuration is GitOps-managed.</p>
            <SyntaxHighlighter
                children={gitApplicationExample}
                style={dracula}
                PreTag="div"
                language={'yaml'}
            />
            <SyntaxHighlighter
                children={helmApplicationExample}
                style={dracula}
                PreTag="div"
                language={'yaml'}
            />
            <p>Keeping the values file in your own repo while pulling the chart from upstream needs
                multiple sources:</p>
            <SyntaxHighlighter
                children={multiSourceExample}
                style={dracula}
                PreTag="div"
                language={'yaml'}
            />

            <h3>Sync policy options</h3>
            <div className={globalStyles.styledTable}>
                <table style={{minWidth: '680px'}}>
                    <thead>
                    <tr>
                        <th>Setting</th>
                        <th>Effect</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><i>no <code>automated</code> block</i></td>
                        <td>Manual: Argo CD shows <code>OutOfSync</code> and waits for someone to sync</td>
                    </tr>
                    <tr>
                        <td><code>automated: {'{}'}</code></td>
                        <td>Sync automatically on every new commit</td>
                    </tr>
                    <tr>
                        <td><code>automated.prune: true</code></td>
                        <td>Also delete resources removed from Git</td>
                    </tr>
                    <tr>
                        <td><code>automated.selfHeal: true</code></td>
                        <td>Also undo manual edits made in the cluster</td>
                    </tr>
                    <tr>
                        <td><code>CreateNamespace=true</code></td>
                        <td>Create the destination namespace if it doesn't exist</td>
                    </tr>
                    <tr>
                        <td><code>ServerSideApply=true</code></td>
                        <td>Use server-side apply — needed for very large objects such as some CRDs</td>
                    </tr>
                    <tr>
                        <td><code>PruneLast=true</code></td>
                        <td>Delete removed resources only after everything else is synced and healthy</td>
                    </tr>
                    <tr>
                        <td><code>ApplyOutOfSyncOnly=true</code></td>
                        <td>Only apply resources that changed — speeds up syncs of big apps</td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <h3>Many apps: ApplicationSet</h3>
            <p>The classic "app of apps" pattern (one Application whose path contains other Application
                manifests) still works, but an ApplicationSet usually does the same with less YAML:</p>
            <SyntaxHighlighter
                children={applicationSetExample}
                style={dracula}
                PreTag="div"
                language={'yaml'}
            />

            <h3>A sensible repo layout</h3>
            <SyntaxHighlighter
                children={repoLayoutExample}
                style={dracula}
                PreTag="div"
                language={'text'}
            />
            <p>CI builds and pushes the image from the application repo, then commits the new image tag to
                this repo (or opens a PR to it). Argo CD takes it from there.</p>

            <h3>Ordering: sync waves &amp; hooks</h3>
            <SyntaxHighlighter
                children={syncWaveExample}
                style={dracula}
                PreTag="div"
                language={'yaml'}
            />
            <p>Hook types are <code>PreSync</code>, <code>Sync</code>, <code>PostSync</code> and{' '}
                <code>SyncFail</code>. Helm chart hooks (<code>helm.sh/hook: pre-install</code> etc.) are mapped
                to the closest Argo CD hook automatically.</p>

            <h3>argocd CLI cheat sheet</h3>
            <div className={globalStyles.styledTable}>
                <table style={{minWidth: '680px'}}>
                    <thead>
                    <tr>
                        <th colSpan="2">Login &amp; context</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><code>argocd login argocd.example.com --sso</code></td>
                        <td>Log in through the configured SSO provider</td>
                    </tr>
                    <tr>
                        <td><code>argocd login --core</code></td>
                        <td>Skip the API server and talk to Kubernetes with your kubeconfig directly</td>
                    </tr>
                    <tr>
                        <td><code>argocd context</code></td>
                        <td>List the Argo CD servers you're logged in to, and switch between them</td>
                    </tr>
                    <tr>
                        <td><code>argocd account get-user-info</code></td>
                        <td>Who you're logged in as, and your groups</td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <div className={globalStyles.styledTable}>
                <table style={{minWidth: '680px'}}>
                    <thead>
                    <tr>
                        <th colSpan="2">Applications</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><code>argocd app list</code></td>
                        <td>Every Application with its sync and health status</td>
                    </tr>
                    <tr>
                        <td><code>argocd app get hello-web</code></td>
                        <td>Details and a per-resource status tree</td>
                    </tr>
                    <tr>
                        <td><code>argocd app get hello-web --hard-refresh</code></td>
                        <td>Force a fresh read and render from Git, ignoring caches</td>
                    </tr>
                    <tr>
                        <td><code>argocd app diff hello-web</code></td>
                        <td>What would change if you synced now</td>
                    </tr>
                    <tr>
                        <td><code>argocd app manifests hello-web --source live</code></td>
                        <td>The manifests as they are in the cluster; <code>--source git</code> for Git's
                            version
                        </td>
                    </tr>
                    <tr>
                        <td><code>argocd app delete hello-web</code></td>
                        <td>Delete the Application and (by default) every resource it manages</td>
                    </tr>
                    </tbody>
                </table>
            </div>
            <SyntaxHighlighter
                children={createApplicationExample}
                style={dracula}
                PreTag="div"
                language={'bash'}
            />

            <div className={globalStyles.styledTable}>
                <table style={{minWidth: '680px'}}>
                    <thead>
                    <tr>
                        <th colSpan="2">Sync &amp; rollback</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><code>argocd app sync hello-web</code></td>
                        <td>Sync now; add <code>--prune</code> to also delete removed resources</td>
                    </tr>
                    <tr>
                        <td><code>argocd app sync hello-web --dry-run</code></td>
                        <td>Go through the motions without applying anything</td>
                    </tr>
                    <tr>
                        <td><code>argocd app sync hello-web --resource apps:Deployment:hello-web</code></td>
                        <td>Sync a single resource (<code>GROUP:KIND:NAME</code>)</td>
                    </tr>
                    <tr>
                        <td><code>argocd app wait hello-web --health --timeout 300</code></td>
                        <td>Block until the app is healthy — useful at the end of a CI pipeline</td>
                    </tr>
                    <tr>
                        <td><code>argocd app history hello-web</code></td>
                        <td>Past syncs, each with an ID and the Git revision deployed</td>
                    </tr>
                    <tr>
                        <td><code>argocd app rollback hello-web 4</code></td>
                        <td>Redeploy the revision with that history ID (requires auto-sync to be off)</td>
                    </tr>
                    <tr>
                        <td><code>argocd app set hello-web --sync-policy none</code></td>
                        <td>Turn auto-sync off, e.g. while debugging or before a rollback</td>
                    </tr>
                    <tr>
                        <td><code>argocd app set podinfo -p replicaCount=3</code></td>
                        <td>Override a Helm parameter on the Application — quick, but now differs from Git</td>
                    </tr>
                    <tr>
                        <td><code>argocd app terminate-op hello-web</code></td>
                        <td>Stop a sync that's stuck (e.g. waiting on a hook that never finishes)</td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <div className={globalStyles.styledTable}>
                <table style={{minWidth: '680px'}}>
                    <thead>
                    <tr>
                        <th colSpan="2">Straight from kubectl</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><code>kubectl -n argocd get applications</code></td>
                        <td>Applications are just CRDs — sync and health status show up as columns</td>
                    </tr>
                    <tr>
                        <td><code>kubectl -n argocd apply -f bootstrap/apps.yaml</code></td>
                        <td>Bootstrap: the one manual apply that hands everything else to Argo CD</td>
                    </tr>
                    <tr>
                        <td><code>kubectl -n argocd logs deploy/argocd-repo-server</code></td>
                        <td>Where Helm/Kustomize rendering errors end up</td>
                    </tr>
                    <tr>
                        <td><code>kubectl -n argocd logs statefulset/argocd-application-controller</code></td>
                        <td>Where sync and diff errors end up</td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <h3>Repositories, clusters &amp; projects</h3>
            <SyntaxHighlighter
                children={connectExample}
                style={dracula}
                PreTag="div"
                language={'bash'}
            />

            <h3>Things that trip people up</h3>
            <ul>
                <li><b><code>kubectl edit</code> doesn't stick</b> with <code>selfHeal</code> on — Argo CD
                    reverts it within seconds. That's the feature; change Git instead.
                </li>
                <li><b>Deleted files don't delete resources</b> unless <code>prune</code> is enabled (in the
                    policy or with <code>sync --prune</code>) — they just show up as needing pruning.
                </li>
                <li><b>Helm charts are rendered with <code>helm template</code></b>, not installed — so{' '}
                    <code>helm list</code> shows nothing, <code>helm rollback</code> doesn't apply, and charts
                    relying on the <code>lookup</code> function see an empty cluster.
                </li>
                <li><b>Permanently <code>OutOfSync</code></b> usually means something else mutates the
                    resource after Argo applies it — an HPA changing replicas, a webhook injecting fields, a
                    controller defaulting values. Use <code>ignoreDifferences</code>:
                    <SyntaxHighlighter
                        children={ignoreDifferencesExample}
                        style={dracula}
                        PreTag="div"
                        language={'yaml'}
                    />
                </li>
                <li><b><code>argocd app rollback</code> refuses to run with auto-sync on</b> — it would just be
                    undone by the next sync. Either <code>git revert</code> the change (the GitOps way) or turn
                    auto-sync off first.
                </li>
                <li><b>Changes take up to ~3 minutes</b> to be noticed, because Argo CD polls Git. Configure a
                    Git webhook pointing at <code>/api/webhook</code> for near-instant syncs.
                </li>
                <li><b>Never commit plain Secrets</b> to the GitOps repo. Pair Argo CD with Sealed Secrets,
                    SOPS or the External Secrets Operator so Git only ever holds encrypted values or references.
                </li>
                <li><b>Deleting an Application without the resources finalizer</b> (when it was created from
                    YAML) leaves its workloads running, orphaned. Add{' '}
                    <code>resources-finalizer.argocd.argoproj.io</code> if you want cascading deletes.
                </li>
            </ul>
        </div>
    )
}
